import React, { useEffect, useRef, useState } from 'react';
import FeeBoardView from './FeeBoard';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../i18n';
import '../styles/global.css';
import { useStore } from '../store';
import { api } from '../utils/api';
import { isValidModule } from '../modules';
import { isSandbox, setSandbox } from '../services/sandboxMode';
import { homePagePath, takeHomeTurn } from '../services/homePage';   // the ≡ menu's Default page (report bbd25b1a)
import Portal from './Portal';
import MailApp from './MailApp';
import ModuleStub from './ModuleStub';
import Admin from './Admin';
import PettyCash from './PettyCash';
import Expense from './Expense';   // the firm's own spending — Expense / Equipment / Asset / Inventory (report 362b5a61)
import BankStatement from './BankStatement';
import Contact from './Contact';
import CalendarView from './Calendar';
import DocStorage from './DocStorage';
import PortalAccounts from './PortalAccounts';
import Services from './Services';
import TakenReturn from './TakenReturn';
import Peak from './Peak';
import Tax from './Tax';
import TaxBatch from './TaxBatch';
import Bookkeeping from './Bookkeeping';
import AccountingBooks from './AccountingBooks';
import Compliance from './Compliance';
import AIWReports from './AIWReports';
import ExternalPortal from './ExternalPortal';
import MarketingPortal from './MarketingPortal';
import ClientPortal from './ClientPortal';
import ContractSign from './ContractSign';
import Leads from './Leads';
import AiMarketing from './AiMarketing';
import DueDiligence, { DDPublicPage } from './DueDiligence';
import { PropertyFormPublicPage } from './PropertyForm';
import Workflows from './Workflows';
import { GlobalTasksAlert } from './MyTasks';
import WfShare from './WfShare';
import PandaMarketing from './PandaMarketing';
import SlfProperty from './SlfProperty';
import ServiceDesk from './ServiceDesk';
import AiCounsel from './AiCounsel';
import StaffDashboard from './StaffDashboard';
import FinanceReport from './FinanceReport';
import ClientDashboard from './ClientDashboard';
import { JobsPage } from './Jobs';
import Toast from './Toast';
import UndoSend from './UndoSend';
import { DocNoHost } from './DocNoLink';
import VersionPage from './VersionPage';
import FileViewer from './FileViewer';
import Bugify from './Bugify';
import DeployNotice from './DeployNotice';   // "the system is being updated" while a deploy restarts it (Bugify deploy-now, 2026-11-03)
import JobStatusConfirm from './JobStatusConfirm';
import JobCommentPing from './JobCommentPing';
import TrPassportPrompt from './TrPassportPrompt';
import CloseJobWatcher from './CloseJobWatcher';
import JoyWidget, { JoyKiosk } from './Joy/JoyWidget';
import UploadIndicator from './UploadIndicator';
import DropProposals from './DropProposals';
import NameChecks from './NameChecks';
import TaxBatchNotice from './TaxBatch/Notice';
import NewMailToast from './NewMailToast';
import ApprovalsArrived from './ApprovalsArrived';
import AiSuggestions from './AiSuggestions';
import AiSuggestionsDigest, { AI_SUGGESTIONS_PATH } from './AiSuggestions/Digest';
import ScrollHint from './ScrollHint';
import FocusDashboard from './FocusDashboard';
import ChatApp from './Chat/ChatApp';
import FilesApp from './FileLibrary/FilesApp';
import EmailApp from './MailApp/EmailApp';
import { brand, brandOr } from '../utils/brand';
import { usePublicBrand, systemTitle } from '../utils/publicBrand';   // the brand before /api/me (GET /api/public/brand)
import { tabColor } from '../tabPalette';
import { setTabDot } from '../utils/tabIcon';

function CenteredSpinner() {
  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-app)' }}>
      <i className="ti ti-loader" style={{ fontSize: 28, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
      <style>{`@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
    </div>
  );
}

function NoAccess() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, background: 'var(--bg-app)' }}>
      <i className="ti ti-lock" style={{ fontSize: 34, color: 'var(--text-tertiary)' }} aria-hidden />
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{t('no_access')}</div>
      <button onClick={() => navigate('/')} style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
        {t('back_to_portal')}
      </button>
    </div>
  );
}

// "Sign in to review & send" on a no-login approval page (owner 2026-10-03, question A item 1) comes here with
// ?return=/api/qa/<token>; after signing in the browser goes back to that page. Only an approval page of
// this same site is accepted — never an arbitrary address.
const approvalReturnPath = (search: string): string => {
  const r = new URLSearchParams(search).get('return') || '';
  return /^\/api\/(qa|oe|da|ta)\/[A-Za-z0-9]{16,128}$/.test(r) ? r : '';
};

// The chat app's own address (report 8c5f3514): the page a phone's Chat icon opens. It is the one address that has
// to come back to itself after a sign-in — somebody who tapped the Chat icon wants Chat, not the Dashboard, and in
// an installed app there is no menu to find their way back with. Same ?return= the approval pages above use, read
// separately because that one hands the browser to an API address and this one is a page of this app.
export const CHAT_PATH = '/chat';
// The Files app's own address (report ac3c43b4): find and open documents on a phone, installed the way Chat is. It
// comes back to itself after a sign-in for the same reason.
export const FILES_PATH = '/files';
// The Email app's own address (report 0e8dd0cb): mail on a phone, installed the way Chat and Files are. It comes back
// to itself after a sign-in for the same reason.
export const EMAIL_PATH = '/email';
/** Whether an address is one of the three phone apps above. None of the office pop-ups is drawn there (report fce7e84d:
 *  "Do not show up notifications (like active jobs…) on phone apps (files and chat)") — see NotOnPhoneApp below, and
 *  services/popupHome, which asks nothing in such a tab and leaves the question to an office tab. */
export const isPhoneApp = (pathname: string): boolean => pathname === CHAT_PATH || pathname === FILES_PATH || pathname === EMAIL_PATH;
const chatReturnPath = (search: string): string => {
  const r = new URLSearchParams(search).get('return');
  return r && isPhoneApp(r) ? r : '';
};

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = approvalReturnPath(location.search);
  const backTo = chatReturnPath(location.search);   // the chat app, which sent them here to sign in
  const { setAuthenticated, loadMe } = useStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const pubBrand = usePublicBrand();

  // Already signed in → go to portal, or to the chat app if that is what asked for a sign-in (unless an approval
  // page sent them here to sign in again).
  useEffect(() => { if (localStorage.getItem('mf_token') && !returnTo) navigate(backTo || '/', { replace: true }); }, []); // eslint-disable-line

  const handle = async () => {
    if (!email || !password) return setError(t('login_fill_fields'));
    setLoading(true); setError('');
    try {
      const res = await api.login(email, password);
      setAuthenticated(res.token);
      if (returnTo) { window.location.assign(returnTo); return; }   // back to the approval page, now signed in
      await loadMe();
      // Back to the chat app (report 8c5f3514), which is what they opened: not a Default page they never asked for.
      if (backTo) { navigate(backTo, { replace: true }); return; }
      // Their own Default page (report bbd25b1a) rather than the Dashboard — the ≡ menu's select, off /api/me; the
      // Dashboard when they have chosen none, or chose a page they may no longer open. Taking the session's turn
      // here means a later visit to the Dashboard leaves them on it (AuthGate).
      const who = useStore.getState().me;
      if (who) takeHomeTurn(who.userId);
      navigate((who && homePagePath(who.homePage, who)) || '/', { replace: true });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-app)' }}>
      <div style={{ width: 380, background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: '32px 28px', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <img src="/logo.png" alt={brandOr('Logo')} style={{ width: 40, height: 40, objectFit: 'contain' }} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{systemTitle(t, pubBrand)}</div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('portal_subtitle')}</div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(returnTo || backTo) && <div style={{ fontSize: 12, color: 'var(--text-secondary)', background: 'var(--c-gray-50)', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{t(returnTo ? 'login_return_approval' : backTo === FILES_PATH ? 'files_login_return' : backTo === EMAIL_PATH ? 'emailapp_login_return' : 'chat_login_return')}</div>}
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{t('emailAddress')}</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@company.com" onKeyDown={e => e.key === 'Enter' && handle()} style={{ width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{t('field_password')}</label>
            <input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="••••••••" onKeyDown={e => e.key === 'Enter' && handle()} style={{ width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none' }} />
          </div>
          {error && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{error}</div>}
          <button onClick={handle} disabled={loading} style={{ padding: '10px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 13, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? t('login_wait') : t('login_signin')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Gate: require a token, then load the current user's profile before rendering.
function AuthGate() {
  const { me, loadMe } = useStore();
  const token = localStorage.getItem('mf_token');
  const [checking, setChecking] = useState(!!token && !me);
  const navigate = useNavigate();
  const loc = useLocation();

  // Their own Default page (report bbd25b1a), for a browser session that opens the app with a token already in it —
  // a sign-in applies it itself (LoginPage). Only this session's FIRST authenticated page load takes the turn, and
  // only a landing on the bare Dashboard address is moved: a refresh of any page keeps its own address (?tab=,
  // /m/<key>, a page's sub-tab), and every later visit to the Dashboard stays on the Dashboard. Decided before
  // `checking` clears, so the page below is drawn at the address it ends up on.
  const land = () => {
    const who = useStore.getState().me;
    if (who && takeHomeTurn(who.userId) && loc.pathname === '/' && !loc.search) {
      const to = homePagePath(who.homePage, who);
      if (to) navigate(to, { replace: true });   // '' = the Dashboard, which is where they already are
    }
    setChecking(false);
  };

  // A deploy restarts the server for about a minute (Bugify deploy-now, 2026-11-03). /api/me failing then is no reason to
  // sign anybody out — loadMe keeps the token when the API is merely unreachable (store/index.ts) — but it leaves no
  // user to draw. So the check runs again as soon as the API answers (slf:api-back, services/apiHealth.ts), and every
  // 5 s as a backstop while the token is there and the user is not.
  const retry = useRef<number | undefined>();
  const tryMe = () => {
    window.clearTimeout(retry.current);
    loadMe().finally(() => {
      land();
      if (localStorage.getItem('mf_token') && !useStore.getState().me) retry.current = window.setTimeout(tryMe, 5000);
    });
  };
  useEffect(() => {
    if (token && !me) tryMe();
    else land();
    const onBack = () => {
      if (!localStorage.getItem('mf_token') || useStore.getState().me) return;
      setChecking(true);
      tryMe();
    };
    window.addEventListener('slf:api-back', onBack);
    return () => { window.removeEventListener('slf:api-back', onBack); window.clearTimeout(retry.current); };
  }, []); // eslint-disable-line

  // No token → the sign-in page. The chat app's address is carried along with it (report 8c5f3514), since an
  // installed Chat has no menu to come back by; every other address drops it as it always has. The Files app's
  // address likewise (report ac3c43b4), and the Email app's (report 0e8dd0cb).
  if (!token) return <Navigate to={isPhoneApp(loc.pathname) ? `/login?return=${loc.pathname}` : '/login'} replace />;
  if (checking || !me) return <CenteredSpinner />;
  // External accountants/auditors only ever see their own portal — never the internal app.
  if (me.role === 'external') return <ExternalPortal />;
  // Marketing role → a mobile-first, fully read-only portal (own clients' basic info + own finance share).
  if (me.role === 'marketing') return <MarketingPortal />;
  // The auditor login (Bugify #3898b242) → SLF Tax only, and none of the office overlays below (Joy,
  // the tasks alert) come along.
  if (me.role === 'auditor') return <AuditorGate />;
  // The virtual accountant's own login is a kiosk — ONLY Joy, no portal — so she can live on any spare
  // desktop. Which login that is: the 'virtual_accountant_mailbox' role (Admin → Roles), not an address here.
  if ((me.roles || []).includes('virtual_accountant_mailbox')) return <JoyKiosk />;
  // Joy floats over every authenticated internal page; the widget self-gates (office users + on).
  // GlobalTasksAlert rides along so an overdue job / long-unmatched receipt reaches whoever is working,
  // whatever module page they are on — it only overlays, never navigates or reloads.
  // DropProposals: the AI's name-and-folder proposal for each document dropped into Drop File, whoever
  // dropped it (report df26fdac) — shown to Calem alone; it self-gates to him.
  // NameChecks: Our Documents' name questions (report 10ab8ab1) — a name that disagrees with the
  // archive's own prevailing spelling, or has lost a space or its company, put up one card at a time
  // with the correction ready to submit; it self-gates to the people entitled to edit that library.
  // NewMailToast: the new-mail pop-up at the bottom right of every page (Calem, report 2e5c8ac7) — new
  // Inbox mail of the user's starred account; it self-gates to the email module.
  // ScrollHint: "Use your mouse wheel to scroll up and down." beside the mouse over a box that scrolls, now that no
  // box shows an up-and-down scroll bar (report d7af4dee) — on the first 10 visits to each page.
  // TaxBatchNotice: "your batch report is ready" — the pile of invoices the accounting bot has just
  // finished reading (Calem, report 044e76b0); it self-gates to the people holding the Tax module.
  // ApprovalsArrived: "New approvals have arrived" — in place of the approval request emails, which no
  // longer go out (report 327a232d); it self-gates to the approvers.
  // AiSuggestionsDigest: the once-a-day digest of the AI improvement suggestions (report 1492665d), which
  // opens the day's list; it self-gates to the logins ticked to receive them in Admin → Users.
  // On the phone apps only Joy and ScrollHint come along: the office pop-ups between them are not drawn
  // there (report fce7e84d, NotOnPhoneApp). The Email app goes without Joy as well: her corner of the screen is where
  // its Compose pill and, in an open email, its Forward pill stand (report 0e8dd0cb), and she floats over both.
  return <><Outlet />{loc.pathname !== EMAIL_PATH && <JoyWidget />}<NotOnPhoneApp><GlobalTasksAlert who={me.email} /><DropProposals /><NameChecks /><TaxBatchNotice /><NewMailToast /><ApprovalsArrived /><AiSuggestionsDigest /></NotOnPhoneApp><ScrollHint /></>;
}

// The auditor's whole app is SLF Tax: '/', every other /m/ page and /admin land on it.
function AuditorGate() {
  const loc = useLocation();
  // Its menu has no Sandbox tile, so a Sandbox switched on before the role existed would have no way off.
  useEffect(() => { if (isSandbox()) setSandbox(false); }, []);
  return loc.pathname === '/m/tax' ? <Outlet /> : <Navigate to="/m/tax" replace />;
}

// The job popups are mounted outside AuthGate (see App), so they are kept off the auditor login —
// SLF Tax only (Bugify #3898b242) — here instead.
function NotForAuditor({ children }: { children: React.ReactElement }) {
  const auditor = useStore(s => s.me?.role === 'auditor');
  return auditor ? null : children;
}

// The office pop-ups stay off the phone apps, /chat, /files and /email (report fce7e84d): the job questions mounted in
// App, and My Tasks (with the payment-slip pop-up), Filing's two, the batch report and new mail in AuthGate. A
// question is not dropped — it is asked in an office tab instead (services/popupHome). The chat's own message
// notices, Joy and the scroll hint are no office pop-ups and are not wrapped in this.
function NotOnPhoneApp({ children }: { children: React.ReactNode }) {
  const loc = useLocation();
  return isPhoneApp(loc.pathname) ? null : <>{children}</>;
}

function RequireModule({ k, children }: { k: string; children: React.ReactElement }) {
  const { me } = useStore();
  if (!me) return null;
  // The Financial Report is admins only (owner 2026-10-03); the marketing-owner pass that used to open it
  // for the person's own tabs is gone — the server's module list is the only door.
  // Straight off the server's list (see the menu in Portal) — an admin already has every module in
  // it, so no role shortcut is needed, and a module reserved to one person stays shut by URL too.
  if (!me.modules.includes(k)) return <NoAccess />;
  return children;
}

// Guard for the container pages (Panda Marketing / SLF Property): they aren't real permissions, so grant
// access if the user holds ANY of the folded-in sub-modules. Each tab is gated again inside the page.
function RequireAny({ ks, children }: { ks: string[]; children: React.ReactElement }) {
  const { me } = useStore();
  if (!me) return null;
  if (me.role !== 'admin' && !ks.some(k => me.modules.includes(k))) return <NoAccess />;
  return children;
}

function RequireAdmin({ children }: { children: React.ReactElement }) {
  const { me } = useStore();
  if (!me) return null;
  if (me.role !== 'admin') return <NoAccess />;
  return children;
}

// /m/:key — stub modules (email has its own route above this one).
function ModuleStubRoute() {
  const { key = '' } = useParams();
  const { me } = useStore();
  if (!isValidModule(key)) return <Navigate to="/" replace />;
  if (!me) return null;
  if (me.role !== 'admin' && !me.modules.includes(key)) return <NoAccess />;
  return <ModuleStub moduleKey={key} />;
}

// Keep the browser tab title in sync with the current page so each open tab is distinguishable
// (instead of every tab showing just "SLF System").
// The tab's icon follows the page too (report 17b55236): a dot in the colour of the page's top-menu button
// (utils/tabIcon.ts). A /m/ page with no such colour — most ≡-menu modules, Bugify among them — takes the white dot
// with a green ring instead, the look its bubble has in the menus (report 07ffc4c6); the logo is left to the
// addresses that are a bubble in no menu — Admin, sign-in and the public pages. SLF Property and its tabs are in that
// first group: report d0b44032 gave them a pink dot of their own and its 2nd opening took it back off again.
function DocumentTitle() {
  const loc = useLocation();
  const { t } = useTranslation();
  const pub = usePublicBrand();   // before /api/me has answered (sign-in and public pages)
  useEffect(() => {
    const b = brand() || pub || '';
    const base = b ? `${b} System` : 'Management System';
    const p = loc.pathname;
    // A Quick Launch dashboard names its own tab, "<open page> · Dashboard <its name>": only FocusDashboard knows the
    // name, and each of its ?item= changes would come back here and put "Quick Launch" back over it (report 9b83b181).
    // Its dot is FocusDashboard's too, the open page's colour.
    if (p === '/focus') return;
    // The file viewer tab is named after the document it is showing (FileViewer, report 24e8581a) — only it
    // knows that name, and '/m/file' here would put the key over it.
    if (p.startsWith('/m/file/')) return;
    const sp = new URLSearchParams(loc.search);
    let label = '';
    let dot: string | undefined;   // the tab's dot; undefined = the logo
    if (p === '/' || p === '') {
      // Same label keys as the Portal's tab strip (DASH_TABS / DOC_TABS / CALENDAR_TAB / EMAIL_TAB / CONTACT_TAB) —
      // a tab missing here falls back to "Legal & Accounting", so every new Portal tab needs an entry.
      // Filing reads "Filing", the word on its top-menu button (DOC_TABS' short), where it read the module's name, "Upload
      // Document" (report dd36c2b9, 3rd opening: "Rename the browser tab from "Upload Document" to "Filing""). Tracking
      // likewise reads "Tracking", its top-menu button's word, not the module's name, "Document Tracking" (report 9b83b181).
      const dash: Record<string, string> = { quotation: 'mod_quotation', invoice: 'mod_invoice', receipt: 'mod_receipt', outbox: 'mod_outbox', approvals_doc: 'tab_approvals', approvals_jobs: 'tab_approvals', email_check: 'tab_approvals', bank_acc: 'mod_bank_acc', bank_legal: 'mod_bank_legal', bank_client: 'mod_bank_client', active_jobs: 'mod_active_jobs', closed_jobs: 'mod_closed_jobs', pettycash: 'mod_pettycash', taken_return: 'tab_taken_return', doc_storage: 'tab_upload_doc', calendar: 'mod_calendar', email: 'tab_email', contact: 'tab_contacts', starred: 'mod_starred', expense: 'mod_accounting' };
      const tab = sp.get('tab') || '';
      label = dash[tab] ? t(dash[tab]) : t('portal_subtitle', { defaultValue: 'Dashboard' });
    } else if (p.startsWith('/m/')) {
      const key = p.split('/')[2] || '';
      dot = tabColor(key);
      // Email reads "Email" like its Portal tab, not the "PandaAI™ Email" module name (Bugify #a2db2014); Contacts
      // likewise reads "Contacts", not the singular "Contact" module name (Bugify #21224c07), and Tracking "Tracking", not
      // "Document Tracking" (report 9b83b181).
      const asTab: Record<string, string> = { email: 'tab_email', contact: 'tab_contacts', taken_return: 'tab_taken_return' };
      label = asTab[key] ? t(asTab[key]) : t(`mod_${key}`, { defaultValue: key });
      if (key === 'tax') {
        const secMap: Record<string, string> = { monthly: 'tx_tab_monthly', halfyear: 'tx_tab_halfyear', eoy: 'tx_tab_eoy', property: 'tx_property_tax' };
        const sec = sp.get('section') || ''; if (secMap[sec]) label = `${label} · ${t(secMap[sec])}`;
      }
    } else if (p === CHAT_PATH) label = t('chat_title');   // the chat app (report 8c5f3514)
    else if (p === FILES_PATH) label = t('files_title');   // the Files app (report ac3c43b4)
    else if (p === EMAIL_PATH) label = t('tab_email');     // the Email app (report 0e8dd0cb)
    else if (p.startsWith('/admin')) label = t('admin_panel', { defaultValue: 'Admin' });
    document.title = label ? `${label} · ${base}` : base;
    // The Portal puts up its own dot: only it knows which tab it has open — with no ?tab= in the address, Active Jobs, or
    // the first tab this user may open (Portal/index.tsx: TabDot). A module page with no colour of its own asks for the
    // ringed dot; the other addresses that reach here keep the logo (see above).
    if (p !== '/' && p !== '') setTabDot(dot, p.startsWith('/m/'));
  }, [loc.pathname, loc.search, t, pub]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <DocumentTitle />
      {/* Folder uploads are driven from a module-level manager, so they keep running while the
          operator moves between modules; this card is their only UI and lives in the shell. */}
      <UploadIndicator />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/dd/view/:token" element={<DDPublicPage />} />{/* public client preview — no auth */}
        <Route path="/property-form/:mode/:token" element={<PropertyFormPublicPage />} />{/* Client Property Form's view / edit links — no auth */}
        <Route path="/w/:token" element={<WfShare />} />{/* public workflow share page — no auth */}
        <Route path="/contract/:token" element={<ContractSign />} />
        <Route path="/portal/*" element={<ClientPortal />} />{/* client invoicing portal — self-managed auth */}{/* public contract e-signing — no auth */}
        <Route element={<AuthGate />}>
          <Route path="/" element={<Portal />} />
          {/* Quick Launch's focus dashboard (report 0b314fb0) — the Portal's button opens it in a new tab. No module of
              its own: each page on it is offered and drawn by the permission it has everywhere else. */}
          <Route path="/focus" element={<FocusDashboard />} />
          {/* Chat, as the app a phone installs (report 8c5f3514) — the same chat window the ≡ menu's button opens
              over a page, here with the whole screen to itself. No module of its own: the chat is open to everyone
              on staff, exactly as its button is. */}
          <Route path={CHAT_PATH} element={<ChatApp />} />
          {/* Files, the documents as an app a phone installs (report ac3c43b4) — find and open only: browse, search,
              preview, download and share. Behind the desktop Filing page's own permission (doc_storage), and its Legal /
              Accounting client folders behind the Staff Dashboard's, where the desktop keeps them (FilesApp), so nobody
              reaches a document here that the desktop would not show them; client, external and the other restricted
              logins never get past AuthGate above to begin with. */}
          <Route path={FILES_PATH} element={<RequireModule k="doc_storage"><FilesApp /></RequireModule>} />
          {/* Email, the same mail as an app a phone installs (report 0e8dd0cb) — behind the desktop page's own permission,
              over the same store and the same send path, so nobody reaches a mailbox here that /m/email would not show. */}
          <Route path={EMAIL_PATH} element={<RequireModule k="email"><EmailApp /></RequireModule>} />
          <Route path="/m/email" element={<RequireModule k="email"><MailApp /></RequireModule>} />
          <Route path="/m/pettycash" element={<RequireModule k="pettycash"><PettyCash /></RequireModule>} />
          {/* The firm's own money (Calem, reports 362b5a61 and eb29e199) — the Accounting page, one address
              per account class so each stays behind its own permission: holding Expenses alone never opens
              the Assets list. Every one of them opens the same page, on the class its address names, with
              the five-bubble bar across the top (components/Expense). Equipment and Inventory are gone from
              here with their pages — their rows and their grants are read as Assets now. */}
          <Route path="/m/income" element={<RequireModule k="income"><Expense kind="income" /></RequireModule>} />
          <Route path="/m/expense" element={<RequireModule k="expense"><Expense kind="expense" /></RequireModule>} />
          <Route path="/m/asset" element={<RequireModule k="asset"><Expense kind="asset" /></RequireModule>} />
          <Route path="/m/liability" element={<RequireModule k="liability"><Expense kind="liability" /></RequireModule>} />
          <Route path="/m/equity" element={<RequireModule k="equity"><Expense kind="equity" /></RequireModule>} />
          <Route path="/m/bank_acc" element={<RequireModule k="bank_acc"><BankStatement scope="accounting" moduleKey="bank_acc" /></RequireModule>} />
          <Route path="/m/bank_legal" element={<RequireModule k="bank_legal"><BankStatement scope="legal" moduleKey="bank_legal" /></RequireModule>} />
          {/* The Client Account had no direct route until 2026-10-11 — it was only reachable from the dashboard
              switch, so a link to /m/bank_client (the payment-slip popup's button for a lease-registration
              slip pinned to the Dedicated Client Account) landed on the "Under development" stub. */}
          <Route path="/m/bank_client" element={<RequireModule k="bank_client"><BankStatement scope="client" moduleKey="bank_client" /></RequireModule>} />
          <Route path="/m/contact" element={<RequireModule k="contact"><Contact /></RequireModule>} />
          <Route path="/m/calendar" element={<RequireModule k="calendar"><div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}><CalendarView /></div></RequireModule>} />
          <Route path="/m/doc_storage" element={<RequireModule k="doc_storage"><DocStorage /></RequireModule>} />
          <Route path="/m/portal_accounts" element={<RequireModule k="portal_accounts"><PortalAccounts /></RequireModule>} />
          <Route path="/m/services" element={<RequireModule k="services"><Services /></RequireModule>} />
          <Route path="/m/taken_return" element={<RequireModule k="taken_return"><TakenReturn /></RequireModule>} />
          <Route path="/m/fee_board" element={<RequireModule k="fee_board"><FeeBoardView /></RequireModule>} />
          <Route path="/m/peak" element={<RequireModule k="peak"><Peak /></RequireModule>} />
          <Route path="/m/tax" element={<RequireModule k="tax"><Tax /></RequireModule>} />
          {/* The accounting bot's batch report (Calem, Bugify #044e76b0): one pile of photos closed with
              ✅ Done in Telegram, with every scan previewable and its three columns editable. Behind the
              Tax permission — these are that module's own lines — and its own address so the chat's link
              and the "it is ready" card can open it straight. */}
          <Route path="/m/tax_batch/:id" element={<RequireModule k="tax"><TaxBatch /></RequireModule>} />
          <Route path="/m/version" element={<VersionPage />} />
          {/* A document on its own, in a tab of our own (Calem, report 24e8581a) — what a Job Documents
              paperclip opens now that the unmatch × is gone from the pill. No module of its own, like
              /m/version: any staff login may read a file out of the library, and the Delete button the
              page offers management is the server's own management-only unlink. */}
          <Route path="/m/file/:id" element={<FileViewer />} />
          <Route path="/m/bugify" element={<Bugify />} />{/* shared module — everyone on staff */}
          <Route path="/m/bookkeeping" element={<RequireModule k="bookkeeping"><Bookkeeping /></RequireModule>} />
          <Route path="/m/accounting_books" element={<RequireModule k="accounting_books"><AccountingBooks /></RequireModule>} />
          <Route path="/m/compliance" element={<RequireModule k="compliance"><AIWReports /></RequireModule>} />
          <Route path="/m/compliance_legacy" element={<RequireModule k="compliance"><Compliance /></RequireModule>} />
          <Route path="/m/leads" element={<RequireModule k="leads"><Leads /></RequireModule>} />
          <Route path="/m/ai_marketing" element={<RequireModule k="ai_marketing"><AiMarketing /></RequireModule>} />
          <Route path="/m/due_diligence" element={<RequireModule k="due_diligence"><DueDiligence /></RequireModule>} />
          <Route path="/m/ai_counsel" element={<RequireModule k="ai_counsel"><AiCounsel /></RequireModule>} />
          <Route path="/m/panda_marketing" element={<RequireAny ks={['leads', 'ai_marketing', 'social_poster', 'compliance']}><PandaMarketing /></RequireAny>} />
          <Route path="/m/slf_property" element={<RequireAny ks={['due_diligence', 'property_transfer', 'poa', 'lease_reg', 'last_will', 'property_form', 'workflows']}><SlfProperty /></RequireAny>} />
          <Route path="/m/workflows" element={<RequireModule k="workflows"><Workflows /></RequireModule>} />
          {/* The three desk consoles (owner 2026-09-23) — Legal Matters' twins on the same engine. */}
          <Route path="/m/slf_visa" element={<RequireModule k="slf_visa"><ServiceDesk desk="visa" /></RequireModule>} />
          <Route path="/m/slf_company" element={<RequireModule k="slf_company"><ServiceDesk desk="company" /></RequireModule>} />
          <Route path="/m/slf_license" element={<RequireModule k="slf_license"><ServiceDesk desk="license" /></RequireModule>} />
          <Route path="/m/active_jobs" element={<RequireModule k="active_jobs"><JobsPage view="active" /></RequireModule>} />
          <Route path="/m/closed_jobs" element={<RequireModule k="closed_jobs"><JobsPage view="closed" /></RequireModule>} />
          <Route path="/m/staff_dashboard" element={<RequireModule k="staff_dashboard"><StaffDashboard /></RequireModule>} />
          <Route path="/m/finance_report" element={<RequireModule k="finance_report"><FinanceReport /></RequireModule>} />
          <Route path="/m/client_dashboard" element={<ClientDashboard />} />
          {/* The AI improvement suggestions' list (report 1492665d) — no module of its own: it is open to the logins
              ticked to receive them in Admin → Users, and the page checks that itself (me.aiSuggestions). */}
          <Route path={AI_SUGGESTIONS_PATH} element={<AiSuggestions />} />
          <Route path="/m/:key" element={<ModuleStubRoute />} />
          <Route path="/admin" element={<RequireAdmin><Admin /></RequireAdmin>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      <Toast />
      {/* A sent email's Undo send countdown, and the composer its Undo opens again. */}
      <UndoSend />
      {/* The quotation / invoice / receipt a clicked QO- / IV- / RE- number opens (DocNoLink). */}
      <DocNoHost />
      {/* Not on the phone apps, /chat, /files and /email (report fce7e84d). */}
      <NotOnPhoneApp>
        {/* Before JobStatusConfirm on purpose: same layer, so a status email waiting on its confirmation
            stays on top of a colleague's question — and of the passport question (report c2de0118):
            move an 11-Month extension to In Process now that its passport is back? */}
        <NotForAuditor><JobCommentPing /></NotForAuditor>
        <NotForAuditor><TrPassportPrompt /></NotForAuditor>
        {/* "Ready to close this job?" wherever the operator is (Calem, report dd0ed51a) — it used to be
            able to appear only inside an opened job panel. Mounted before JobStatusConfirm for the same
            reason as the two above. */}
        <NotForAuditor><CloseJobWatcher /></NotForAuditor>
        <NotForAuditor><JobStatusConfirm /></NotForAuditor>
      </NotOnPhoneApp>
      {/* Outside every gate: the staff pages, the phone apps, the sign-in page and the clients' portal all show that the
          system is being updated, so a deploy's restart never looks like a dead system (Bugify deploy-now, 2026-11-03). */}
      <DeployNotice isPhoneApp={isPhoneApp} />
    </BrowserRouter>
  );
}
